// public/js/features/pois/poi-overlay-editor.js
// Overlay list mounted in the POI modal (edit mode): add / edit / remove custom
// map overlays and set the per-user opacity. Adding or editing launches the
// two-phase live calibrator (poi-overlay-calibrator.js). See docs/poi-custom-maps.md.
import { updatePoi } from './api.js';
import { overlayOpacity, setOverlayOpacity } from './poi-overlay-layer.js';
import { getMapInstance } from '../../modules/map/index.js';
import { openOverlayCalibrator } from './poi-overlay-calibrator.js';

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// ctx = { poiId, overlays, hide(), show(), onChanged(overlays) }
export function mountOverlaySection(container, ctx) {
  const state = { overlays: (ctx.overlays || []).map(o => ({ ...o })) };
  const liveMap = () => { try { return getMapInstance(); } catch { return null; } };

  const patch = async () => {
    try {
      const r = await updatePoi(ctx.poiId, { overlays: state.overlays });
      state.overlays = (r?.poi?.overlays || state.overlays).map(o => ({ ...o }));
      ctx.onChanged?.(state.overlays);
    } catch (e) { window.showToast?.(`Overlay save failed: ${e.message}`, 'danger'); }
  };

  function launch({ existing = null, newImage = null }) {
    openOverlayCalibrator({
      map: liveMap(), poiId: ctx.poiId, existing, newImage,
      hide: ctx.hide, show: ctx.show,
      onDone: async (overlay) => {
        if (!overlay) return;
        const i = state.overlays.findIndex(o => o.id === overlay.id);
        if (i >= 0) state.overlays[i] = overlay; else state.overlays.push(overlay);
        await patch();
        window.showToast?.(existing ? 'Overlay updated' : 'Overlay added', 'success');
        render();
      },
    });
  }

  function render() {
    container.innerHTML = `
      <label class="form-label fw-semibold mb-1">Map overlays</label>
      <div class="form-text mb-2">Georeferenced images (e.g. an airport diagram) drawn above the base map, below pins. Each user sets their own opacity.</div>
      <div id="ov-list" class="mb-2"></div>
      <input type="file" id="ov-file" accept="image/png,image/jpeg,image/webp" class="d-none">
      <button type="button" id="ov-add" class="btn btn-sm btn-outline-primary"><i class="fa-solid fa-plus me-1"></i>Add map overlay</button>
    `;
    renderList();
    const file = container.querySelector('#ov-file');
    container.querySelector('#ov-add').addEventListener('click', () => file.click());
    file.addEventListener('change', () => {
      const f = file.files?.[0]; if (!f) return;
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => launch({ newImage: {
          dataURL: reader.result, contentType: f.type,
          width: img.naturalWidth, height: img.naturalHeight,
          name: (f.name || 'Overlay').replace(/\.[^.]+$/, ''),
        } });
        img.src = reader.result;
      };
      reader.readAsDataURL(f);
      file.value = '';
    });
  }

  function renderList() {
    const el = container.querySelector('#ov-list');
    if (!state.overlays.length) { el.innerHTML = '<div class="text-muted small mb-1">No overlays yet.</div>'; return; }
    el.innerHTML = state.overlays.map((o, i) => `
      <div class="d-flex align-items-center gap-2 border rounded p-2 mb-1" data-ov="${i}">
        <i class="fa-solid fa-image text-muted"></i>
        <div class="flex-grow-1 min-w-0">
          <div class="small fw-semibold text-truncate">${esc(o.name || 'Overlay')}</div>
          <div class="d-flex align-items-center gap-2">
            <span class="text-muted" style="font-size:.7rem;">Opacity</span>
            <input type="range" min="0" max="1" step="0.05" value="${overlayOpacity(o)}" class="form-range ov-op" style="max-width:140px;">
          </div>
        </div>
        <button type="button" class="btn btn-sm btn-outline-secondary ov-edit" title="Edit / recalibrate"><i class="fa-solid fa-pen"></i></button>
        <button type="button" class="btn btn-sm btn-outline-danger ov-del" title="Remove"><i class="fa-solid fa-trash"></i></button>
      </div>`).join('');
    el.querySelectorAll('.ov-op').forEach((r, i) => r.addEventListener('input', () => setOverlayOpacity(liveMap(), state.overlays[i], r.value)));
    el.querySelectorAll('.ov-edit').forEach((b, i) => b.addEventListener('click', () => launch({ existing: state.overlays[i] })));
    el.querySelectorAll('.ov-del').forEach((b, i) => b.addEventListener('click', async () => {
      state.overlays.splice(i, 1); await patch(); render();
    }));
  }

  // The calibrator commits on its own Save, so nothing is pending on POI save.
  container.__overlayCommitPending = async () => {};

  render();
}
